import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import styles from '~/styles/NodeIntellica.module.css';
import { ChatMessage } from './NodeIntellica';

interface NodeIntellicaInteractiveProps {
  message: ChatMessage;
}

const NodeIntellicaInteractive: React.FC<NodeIntellicaInteractiveProps> = ({ message }) => {
  const [currentOptions, setCurrentOptions] = useState(message.interactive?.options || []);
  const [selectedPath, setSelectedPath] = useState<{ label: string; value: string }[]>([]);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const hasProcessedRef = useRef(false);

  if (!message.interactive || message.interactive.type !== 'tree') return null;

  const { callback } = message.interactive;
  const isCallbackValid = typeof callback === 'function';

  const handleSelection = (option: { label: string; value: string; description?: string; subOptions?: any[] }) => {
    if (hasProcessedRef.current) return;
    hasProcessedRef.current = true;

    const newPath = [...selectedPath, { label: option.label, value: option.value }];
    setSelectedPath(newPath);

    if (option.subOptions?.length && newPath.length < 10) {
      setCurrentOptions(option.subOptions);
      hasProcessedRef.current = false;
    } else if (isCallbackValid) {
      setSelectedValues((prev) => {
        const updated = [...prev, option.value];
        console.log(`[${message.id}] Calling callback with values: ${updated}`);
        callback(updated); // Call callback with all selected values
        return updated;
      });
      // Don’t clear currentOptions yet; wait for confirmation
    } else {
      console.warn(`[${message.id}] Callback for ${option.value} is not a function`);
      hasProcessedRef.current = false;
    }
  };

  const toggleSelection = (option: { label: string; value: string; description?: string; subOptions?: any[] }) => {
    setSelectedValues((prev) => {
      const updated = prev.includes(option.value)
        ? prev.filter((v) => v !== option.value)
        : [...prev, option.value];
      console.log(`[${message.id}] Toggled selection: ${updated}`);
      return updated;
    });
  };

  const goBack = () => {
    const newPath = selectedPath.slice(0, -1);
    setSelectedPath(newPath);

    let opts = message.interactive.options;
    for (const { value } of newPath) {
      const next = opts.find((o: any) => o.value === value)?.subOptions || [];
      opts = next.length ? next : opts;
    }
    setCurrentOptions(opts);
    hasProcessedRef.current = false;
  };

  const confirmSelections = () => {
    if (isCallbackValid && selectedValues.length > 0) {
      console.log(`[${message.id}] Confirming selections: ${selectedValues}`);
      callback(selectedValues);
      setCurrentOptions([]);
    }
  };

  return (
    <div className={styles.treeContainer}>
      {selectedPath.length > 0 && (
        <Button onClick={goBack} className={styles.backButton}>Back</Button>
      )}
      <div className={styles.buttonOptions}>
        {currentOptions.map((opt) => (
          <div key={opt.value} className={styles.optionWrapper}>
            <Button
              onClick={() => (opt.subOptions?.length ? handleSelection(opt) : toggleSelection(opt))}
              className={`${styles.optionButton} ${selectedValues.includes(opt.value) ? styles.selected : ''}`}
              disabled={!isCallbackValid && !opt.subOptions?.length}
            >
              {opt.label}
            </Button>
            {opt.description && (
              <div className={styles.optionDescription}>{opt.description}</div>
            )}
          </div>
        ))}
      </div>
      {selectedValues.length > 0 && !currentOptions.some(opt => opt.subOptions?.length) && (
        <Button onClick={confirmSelections} className={styles.confirmButton}>
          Confirm Selections
        </Button>
      )}
      {selectedPath.length > 0 && (
        <div className={styles.selectedPath}>
          Selected Path: {selectedPath.map((item) => item.label).join(' > ')}
        </div>
      )}
      {selectedValues.length > 0 && (
        <div className={styles.selectedValues}>
          Selected Values: {selectedValues.join(', ')}
        </div>
      )}
    </div>
  );
};

export default NodeIntellicaInteractive;